<i18n src="./resources/locales.json"></i18n>
<template>
  <div>
    <div class="primary-title xtium-title">{{ $t('customLogo') }}</div>
    <div class="d-flex flex-column justify-center mb-2">
      <div
        id="file-drop"
        @drop.prevent="onFileDrop"
        @dragover.prevent="onDragOver"
        @dragleave.prevent="onDragLeave"
        :class="{ 'text-align-center': true, 'drop-logo-border': true, 'pa-2': true, 'drag-over': dragOver }"
      >
        <input
          id="upload-input"
          ref="uploadInput"
          type="file"
          name="filename"
          accept="image/png, image/jpeg"
          hidden
          @change="onInputFileSelect"
        />
        <div>
          <v-icon x-large>mdi-folder</v-icon>
        </div>
        <div>{{ $t('dropLogo') }}</div>
      </div>
      <div class="text-align-center my-2">- {{ $t('or') }} -</div>
      <v-btn color="primary" :disabled="disableControls" @click="uploadFiles">{{ $t('selectFile') }}</v-btn>
      <div class="error--text">
        <div v-if="showNoFileError">{{ $t('selectAFile') }}</div>
        <div v-if="showSizeError">{{ $t('maxFileSize', [(logoSize / 1048576).toFixed(1)]) }}</div>
        <div v-if="showPayloadSizeError">{{ $t('maxPayloadSize') }}</div>
      </div>
    </div>
    <v-row v-if="objectUrl">
      <v-col class="text-center py-0">
        <div class="image-container">
          <img class="image-preview" ref="source" alt="Preview" :src="objectUrl" />
        </div>
        <v-row class="justify-center">
          <v-col class="flex-grow-0 py-0">
            <v-btn icon small class="d-flex justify-center" @click="resetCropper">
              <v-icon small>mdi-aspect-ratio</v-icon>
            </v-btn>
          </v-col>
          <v-col class="flex-grow-0 py-0">
            <v-btn icon small class="d-flex justify-center" @click="rotateLeft">
              <v-icon small>mdi-rotate-left</v-icon>
            </v-btn>
          </v-col>
          <v-col class="flex-grow-0 py-0">
            <v-btn icon small class="d-flex justify-center" @click="rotateRight">
              <v-icon small>mdi-rotate-right</v-icon>
            </v-btn>
          </v-col>
          <v-col class="flex-grow-0 py-0">
            <v-btn icon small class="d-flex justify-center" @click="zoomIn">
              <v-icon small>mdi-plus</v-icon>
            </v-btn>
          </v-col>
          <v-col class="flex-grow-0 py-0">
            <v-btn icon small class="d-flex justify-center" @click="zoomOut">
              <v-icon small>mdi-minus</v-icon>
            </v-btn>
          </v-col>
        </v-row>
      </v-col>
    </v-row>
  </div>
</template>
<script>
import Cropper from 'cropperjs';
import { debounce } from 'lodash';
import { mapGetters } from 'vuex';
import { maxLogoFileSize } from '../../classes/theme';
import EcsDisableControlsMixin from '@/mixins/EcsDisableControlsMixin';

export default {
  name: 'EcsFilePicker',
  mixins: [EcsDisableControlsMixin],
  computed: {
    ...mapGetters(['theme']),
    showNoFileError() {
      return !this.theme.getLogoIsValid && !this.selectedFile;
    }
  },
  data() {
    return {
      cropper: null,
      objectUrl: null,
      previewCropped: null,
      selectedFile: null,
      debouncedUpdatePreview: debounce(this.updatePreview, 250),
      zoom: 0.05,
      showSizeError: false,
      dragOver: false,
      logoSize: maxLogoFileSize,
      showPayloadSizeError: false
    };
  },
  watch: {
    previewCropped() {
      this.emitPreviewCropped(this);
    }
  },
  created() {
    this.$root.$on('clear-file-input', this.clearInput);
  },
  methods: {
    onDragOver() {
      this.dragOver = true;
    },
    onDragLeave() {
      this.dragOver = false;
    },
    onFileDrop(event) {
      if (!this.disableControls) {
        event.preventDefault();
        this.dragOver = false;
        const selectedFile = event.dataTransfer.items[0].getAsFile();
        this.setupCropper(selectedFile);
      }
    },
    onInputFileSelect() {
      const selectedFile = this.$refs.uploadInput.files[0];
      this.setupCropper(selectedFile);
    },
    uploadFiles() {
      this.$refs.uploadInput.click();
    },
    resetCropper() {
      this.cropper.reset();
    },
    rotateLeft() {
      this.cropper.rotate(-90);
    },
    rotateRight() {
      this.cropper.rotate(90);
    },
    setupCropper(selectedFile) {
      if (selectedFile && selectedFile.size > maxLogoFileSize) {
        this.showSizeError = true;
        return;
      }
      this.showSizeError = false;
      if (this.cropper) {
        this.cropper.destroy();
      }
      if (this.objectUrl) {
        window.URL.revokeObjectURL(this.objectUrl);
      }
      if (!selectedFile) {
        this.cropper = null;
        this.objectUrl = null;
        this.previewCropped = null;
        return;
      }
      this.objectUrl = window.URL.createObjectURL(selectedFile);
      this.$nextTick(this.setupCropperInstance);
    },
    setupCropperInstance() {
      this.cropper = new Cropper(this.$refs.source, {
        aspectRatio: 16 / 9,
        crop: this.debouncedUpdatePreview
      });
    },
    updatePreview() {
      const canvas = this.cropper.getCroppedCanvas();
      this.previewCropped = canvas.toDataURL('image/png');
    },
    emitPreviewCropped: debounce(self => {
      // Maximum image size of 15mb
      if (self.previewCropped.length < 15728640) {
        self.setPayloadSizeError(false);
        self.$emit('previewChange', self.previewCropped);
      } else {
        self.setPayloadSizeError(true);
      }
    }, 200),
    zoomIn() {
      this.cropper.zoom(this.zoom);
    },
    zoomOut() {
      this.cropper.zoom(-this.zoom);
    },
    clearInput() {
      if (this.cropper) {
        this.cropper = null;
        this.objectUrl = null;
        this.previewCropped = null;
      }
      this.selectedFile = null;
    },
    setPayloadSizeError(value) {
      this.showPayloadSizeError = value;
    }
  }
};
</script>
<style scoped>
.image-container {
  display: inline-block;
}
.image-preview {
  display: block;
  max-height: 229px;
  max-width: 100%;
}
.card-container {
  width: 100%;
}
.text-align-center {
  text-align: center;
}
.drop-logo-border {
  border: 1px dashed white;
  border-radius: 8px;
}
.drag-over {
  background-color: rgba(128, 128, 128, 0.75);
}
</style>
<style src="cropperjs/dist/cropper.css" lang="css"></style>
